import { Inject } from '@odinjs/odin';
import { ContextualBarAPI, DeckAPI, DeckValue, FetchAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, CredentialsValidationError, CredentialsValidationSuccess, foundationContext } from '@philips-internal/oswald-ui-foundation';

import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import { AbortErrorInterface } from '../../../interfaces/double-conference/AbortErro';
import { ItemVO } from '../../../interfaces/double-conference/ItemVO';
import { Solution } from '../../../interfaces/double-conference/Solution';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { DoubleConferenceUtils } from '../utils/double-conference-utils';

import './handling-order-items-controller.css';

interface JWTToken {
    [key: string]: unknown;
    sub: string;
}

@Controller({ component: 'handling-order-items-layout', fragment: 'handling-order-items',})
class HandlingOrderItemsController extends BaseController {
  @Inject('handling-order-card-deck')
  deckCardsApi!: DeckAPI;

  @Inject('fetch-handling-order')
  fetchCardsApi!: FetchAPI;

  @OnMount({ target: 'contextual-bar-double-check' })
  onContextualBarMount(evt: MountEvent): void {
    const contextualBar = evt.target as ContextualBarAPI;
    InformationBarUtils.collapsedBar = true;

    const {patientId, encounterId, numberOrderManipulation} = this.historyService.getCurrentRouteParams();

    this.context.set({ encounterId, patientId});

    const titleContextualBar = MessageService.translate('text.order-manipulation-with-macro').replace('#@ORDEM_MANIPULACAO#@', `${numberOrderManipulation}`);

    contextualBar.setTitle(titleContextualBar);
  }

  @OnMount({ target: 'fetch-handling-order' })
  handleFetchItems(): void {

    const {numberOrderManipulation, numberPrescription, solutionSequence} = this.historyService.getCurrentRouteParams();

    const params = {
      numberOrderManipulation: Number(numberOrderManipulation),
    } as Record<string, Value>;

    DoubleConferenceUtils.validateParams(params, Number(numberPrescription), Number(solutionSequence));

    const route: Endpoint = this.validateParamsToEndPoint(
      Number(numberPrescription),
      Number(solutionSequence)
    );

    this.fetchCardsApi.setEndpoint(route);
    this.fetchCardsApi.setParameters(params);

  }

  @OnAfterFetch({target: 'fetch-handling-order'})
  handleItemsAfterFech(fetchEvent: FetchEvent): void {
    this.loadDataCards(fetchEvent);
  }

  loadDataCards(fetchEvent: FetchEvent): void{

    const data: DeckValue[] = [];
    const {solutionSequence} = this.historyService.getCurrentRouteParams();
    const isSolution =  Number(solutionSequence) ? true : false;
    let idCard = 1;

    if (isSolution) {
      const solution = fetchEvent.data as Solution;
      data.push({
        dilutionDescription: solution.dilutionDescription,
        doseQuantity: solution.doseQuantity,
        id: idCard,
        isSolution: true,
        itemDescription: solution.itemDescription,
        prescriptionDescription: solution.prescriptionDescription,
        unitMeasureDoseCode: solution.unitMeasureDoseCode,
      });
    } else {
      const medications = fetchEvent.data as ItemVO[];
      medications.forEach((item) =>
        data.push({
          dilutionDescription: item.dilutionDescription,
          id: idCard++,
          itemDescription: item.itemDescription,
          prescriptionDescription: item.prescriptionDescription,
        })
      );
    }

    this.deckCardsApi.setData(data);
  }

  @OnClick({ target: 'handling-order-items-button' })
  onClickButtonConfirmation(): void {
    foundationContext.promptCredentialsValidation({
      onDismiss: () => {},
      onError: (event) => this.validateCredentialsWithError(event),
      onSuccess: (event) => this.validateCredentialsWithSuccess(event),
      validateButtonExpression: MessageService.translate('$expression.$702951'),
    });
  }

  validateCredentialsWithSuccess(event: CredentialsValidationSuccess): void {
    const {numberOrderManipulation, processSequenceNumber} = this.historyService.getCurrentRouteParams();
    const payloadSymbol = Object.getOwnPropertySymbols(event.token).find(s => s.description === 'payload');
    const userValidateCredentials = payloadSymbol && ((event.token as unknown) as { [key: symbol]: JWTToken })[payloadSymbol]
      ? ((event.token as unknown) as { [key: symbol]: JWTToken })[payloadSymbol].sub
      : ' ';
    const userName = foundationContext.getUser()?.username;

    if(userValidateCredentials !== userName) {

      const body = {
        numberOrderManipulation: numberOrderManipulation,
        processSequenceNumber: processSequenceNumber,
        userName: userValidateCredentials,
      } as Record<string, Value>;

      HttpService.post(Endpoint.CALL_PROCEDURE_CHECK_PREPARATION, {}, { body })
        .then(() => {
          this.showToast('text.checked-item', ToastLevel.SUCCESS);
          applicationContext.redirect(ApplicationRoute.DOUBLE_CONFERENCE + ApplicationRoute.VALIDATE_HANDLING_ORDER);
          return Promise.resolve(true);
        })
        .then(redirect => {
          redirect && applicationContext.clearNavigationStack();
        })
        .catch(error => this.tryToShowAbortMessages(error as AbortErrorInterface));
    } else {
      this.showToast('text.user-conference-not-allowed',ToastLevel.WARNING);
      return;
    }
  }

  remainingTries = 3;

  validateCredentialsWithError(event: CredentialsValidationError): void {
    const { abort } = event;
    const errorValidate = 'login-message.invalid-user-password-text';

    this.showToast(errorValidate, ToastLevel.ERROR);

    if (this.remainingTries > 0) {
      this.remainingTries = this.remainingTries - 1;
    }

    if (this.remainingTries === 0) {
      abort();
    }
  }

  validateParamsToEndPoint(numberPrescription: number, solutionSequence: number): Endpoint {
    if (numberPrescription && solutionSequence) {
      return Endpoint.GET_ITEM_SOLUTION_WITH_SEQSOLUTION_AND_NUMBERPRESC;
    }
    if (solutionSequence) {
      return Endpoint.GET_ITEM_SOLUTION_WITH_SEQSOLUTION;
    }
    return Endpoint.GET_MEDICATION_DC;
  }

  tryToShowAbortMessages(abortError: AbortErrorInterface): void {
    if (abortError.body && abortError.body.errors) {
      abortError.body.errors.forEach((errorMessage) =>
        this.showToast(errorMessage, ToastLevel.ERROR)
      );
    }
  }

  showToast(message: string, level: ToastLevel): void {
    const toastId = message;
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: level,
      message: MessageService.translate(message),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }
}

export default HandlingOrderItemsController;
